import { instrumentRunQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { getSelectedOrg } from '@/helpers';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { searchStudies } from '@/services/studies';
import type { InstrumentRunPublic, Org, PaginatedStudiesResponse } from '@/types';
import { createFileRoute, redirect } from '@tanstack/react-router';
import React from 'react';
import { z } from 'zod';

import {
  STUDIES_QUERY_GC_TIME,
  STUDIES_QUERY_STALE_TIME,
  INSTRUMENT_RUN_DRAFT_STALE_TIME,
  INSTRUMENT_RUN_GC_TIME,
} from '../../constants';

// Define allowed modes for extensibility
const modes = ['create', 'draft', 'edit'] as const;

const newInstrumentRunSearchSchema = z.object({
  mode: z.enum(modes).optional().default('create'),
  id: z.string().optional(), // ID of the draft/run to edit
});

export const Route = createFileRoute('/instrument_runs/new')({
  validateSearch: newInstrumentRunSearchSchema,
  // Always fetch fresh data - bypass router cache
  // This ensures SampleManager and form always have the latest backend data
  staleTime: 0,
  beforeLoad: ({ context }) => {
    // Redirect to login if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }

    // Redirect if no organization is selected
    const selectedOrg = getSelectedOrg();
    if (!selectedOrg) {
      throw redirect({
        to: '/organizations',
      });
    }
  },

  loaderDeps: ({ search }) => ({
    mode: search.mode,
    id: search.id,
  }),

  loader: async ({ context, deps }) => {
    const selectedOrg = getSelectedOrg();
    const orgId = selectedOrg?.id;

    if (!orgId) {
      throw redirect({
        to: '/organizations',
      });
    }

    // Prefetch studies data
    try {
      await context.queryClient.prefetchQuery<PaginatedStudiesResponse>({
        queryKey: studyQueryKeys.list(orgId),
        queryFn: async () => {
          return searchStudies(orgId, {
            skip: 0,
            limit: 500,
            sort_by: 'created_at',
            sort_order: 'desc',
          });
        },
        staleTime: STUDIES_QUERY_STALE_TIME,
        gcTime: STUDIES_QUERY_GC_TIME,
      });
    } catch {
      // Prefetch failed — ignore and continue; no console logging.
    }

    // If mode is 'draft' or 'edit', fetch the instrument run data from backend
    let instrumentRunData: InstrumentRunPublic | null = null;

    if ((deps.mode === 'draft' || deps.mode === 'edit') && deps.id) {
      const runId = deps.id; // Type narrowing - we know deps.id is defined here
      let fetchError: unknown = null;
      try {
        instrumentRunData = await context.queryClient.fetchQuery<InstrumentRunPublic>({
          queryKey: instrumentRunQueryKeys.detail(orgId, runId),
          queryFn: () => fetchInstrumentRun(orgId, runId),
          staleTime: INSTRUMENT_RUN_DRAFT_STALE_TIME,
          gcTime: INSTRUMENT_RUN_GC_TIME,
        });
      } catch (error) {
        fetchError = error;
      }
      if (fetchError) {
        // If the error is a redirect, rethrow it
        if (
          typeof fetchError === 'object' &&
          fetchError instanceof Error &&
          '__tanstack_router_redirect' in fetchError
        ) {
          throw fetchError;
        }
        // Fetch failed — redirecting to list. Error not logged to console.
        throw redirect({
          to: '/instrument_runs',
        });
      }
      if (!instrumentRunData) {
        // No run found for the given id: redirect (do not continue to page with null)
        throw redirect({
          to: '/instrument_runs',
          // Optionally add a search param or notification for not found
        });
      }
      // If mode is 'edit' but status is 'DRAFT', update mode in URL
      if (deps.mode === 'edit' && instrumentRunData.status === 'DRAFT') {
        throw redirect({
          to: '/instrument_runs/new',
          search: { mode: 'draft', id: runId },
        });
      }
      // If mode is 'draft' but status is not 'DRAFT', redirect to detail view
      if (deps.mode === 'draft' && instrumentRunData.status !== 'DRAFT') {
        throw redirect({
          to: '/instrument_runs/$instrumentRunId',
          params: { instrumentRunId: runId },
        });
      }
    }

    let organizations: Org[] = [];
    try {
      organizations = await fetchOrganizations();
    } catch {
      // Failed to fetch organizations — proceed with empty list; no logging.
    }

    return {
      orgId,
      mode: deps.mode,
      formInstrumentRunData: instrumentRunData, // Fixed key to match UI expectation
      organizations,
    };
  },
});
